import { api, type NativeTransport } from '../api/client.ts';
import { apiOrigin, isNative, nativePlatform, sessionToken } from '../api/origin.ts';
import { registerVoipToken } from './callKit.ts';
import { reportClientError } from './errorReporter.ts';
import { PLUGIN_TIMEOUT_MS, withTimeout } from './gallery.ts';
import {
  nativePushPlugin,
  rememberPushToken,
  storedPushToken,
  type NativePermission,
} from './nativePush.ts';

export interface PushEnvironment {
  /**
   * There is a way to be notified here at all.
   *
   * Two different facts wearing one name, deliberately: on the web it is the
   * browser having the service worker and push APIs, and in the app it is the
   * native plugin being present. Everything that reads this only wants to know
   * whether to offer the switch, and the distinction below is for the sentence
   * shown when the answer is no.
   */
  supported: boolean;
  /**
   * Running inside the native shell. There is no service worker on
   * `capacitor://localhost`, so notifications arrive through APNs and the OS
   * rather than through `client/src/sw.ts`. Worth distinguishing, because
   * «этот браузер не поддерживает уведомления» inside an app reads as a fault.
   */
  isNative: boolean;
  isIos: boolean;
  /** Launched from the home screen rather than a browser tab. */
  isStandalone: boolean;
  /**
   * iOS only delivers push to a home-screen install, so a tab has to be told to
   * install first rather than shown a permission prompt that cannot work.
   */
  needsHomeScreen: boolean;
  permission: NotificationPermission | 'unsupported';
}

export type EnableResult =
  | { ok: true; message: string }
  | { ok: false; reason: 'unsupported' | 'needs-home-screen' | 'denied' | 'failed'; message: string };

/**
 * navigator.standalone is the iOS-only signal and predates display-mode, which
 * older iOS versions do not report reliably — so both are consulted.
 */
function detectStandalone(): boolean {
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return iosStandalone || window.matchMedia('(display-mode: standalone)').matches;
}

/**
 * navigator.platform is deprecated and an iPad reports itself as MacIntel, so
 * the touch-point count is what separates it from a real Mac.
 */
function detectIos(): boolean {
  if (/iPad|iPhone|iPod/.test(navigator.userAgent)) return true;
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

/**
 * The app's notification permission, as last seen.
 *
 * Cached because iOS answers this asynchronously and `pushEnvironment()` is
 * read synchronously from render — a useState initialiser in two components.
 * The value is refreshed by `refreshEnvironment()` below, which every screen
 * that shows it calls on mount and after anything that could have changed it,
 * so the window in which this is stale is somebody leaving for iOS Settings and
 * coming back. That costs one wrong sentence until the next open, and the
 * alternative costs every caller an await.
 */
let nativePermission: NativePermission = 'default';

export function pushEnvironment(): PushEnvironment {
  const isIos = detectIos();
  const isStandalone = detectStandalone();

  if (isNative) {
    const supported = nativePushPlugin() !== null;
    return {
      supported,
      isNative,
      isIos,
      isStandalone,
      // Never inside the shell, and the guard is not cosmetic: a WKWebView is
      // an iPhone by user agent and is not standalone by either signal, so
      // without this the app would tell the person holding it to add it to
      // their home screen — which is where they already got it.
      needsHomeScreen: false,
      permission: supported ? nativePermission : 'unsupported',
    };
  }

  const supported =
    'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

  return {
    supported,
    isNative,
    isIos,
    isStandalone,
    needsHomeScreen: isIos && !isStandalone,
    permission: supported ? Notification.permission : 'unsupported',
  };
}

/**
 * The same picture, having asked the OS first.
 *
 * On the web this is `pushEnvironment()` and an await that does nothing — the
 * browser's permission is a synchronous property. It exists for the app, where
 * it is the only way the cache above is ever filled in.
 */
export async function refreshEnvironment(): Promise<PushEnvironment> {
  const plugin = nativePushPlugin();
  if (plugin) {
    try {
      const { permission } = await withTimeout(
        plugin.permission(),
        PLUGIN_TIMEOUT_MS,
        'разрешение на уведомления',
      );
      nativePermission = permission;
    } catch {
      // A native half older than this bundle, whose bridge does not answer at
      // all. `default` is the honest report: the switch stays on «Включить»,
      // and pressing it produces a sentence rather than silence.
    }
  }
  return pushEnvironment();
}

/**
 * The applicationServerKey has to be raw bytes, not the base64url string. The
 * buffer is spelled out in the type because a plain `Uint8Array` widens to
 * ArrayBufferLike, which BufferSource does not accept.
 */
function decodeKey(base64Url: string): Uint8Array<ArrayBuffer> {
  const padded = base64Url.padEnd(base64Url.length + ((4 - (base64Url.length % 4)) % 4), '=');
  const binary = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}

/**
 * Which registry this phone's token comes from — and therefore which half of the
 * server has to be configured for it to be worth asking for one.
 *
 * The two halves are independent on the server: a deployment may have an Apple
 * key and no Firebase project, or the reverse. So reading the wrong flag is not
 * a cosmetic mistake, it is an app that refuses to register a perfectly good
 * token and says «сервер не настроен» about a server that is.
 *
 * Everything that is not Android is Apple, rather than the other way round,
 * because 'unknown' is a real answer here — see `nativePlatform`. A shell too
 * old to name itself is an iOS one: the Android shell did not exist before that
 * method did.
 */
function nativeTransport(): NativeTransport {
  return nativePlatform() === 'android' ? 'fcm' : 'apns';
}

/**
 * Registers this app with its platform's push service and hands the server the
 * token.
 *
 * Both halves matter and only one of them involves the person holding the
 * phone. The system alert is asked for once, ever; the token is asked for on
 * every launch, because it changes under a restore or a reinstall and nothing
 * tells the app that it has — the same reason `syncSubscription` re-posts a Web
 * Push subscription on the web.
 *
 * Returns the outcome rather than throwing, because there are three of them and
 * the screen has a different sentence for each.
 */
async function registerNative(): Promise<EnableResult> {
  const plugin = nativePushPlugin();
  if (!plugin) {
    return {
      ok: false,
      reason: 'unsupported',
      message: 'Эта версия приложения не умеет получать уведомления. Обновите её.',
    };
  }

  const transport = nativeTransport();

  const config = await api.pushConfig();
  if (!config[transport]) {
    return {
      ok: false,
      reason: 'failed',
      message: 'Уведомления недоступны: сервер не настроен.',
    };
  }

  const result = await withTimeout(plugin.register(), PLUGIN_TIMEOUT_MS, 'уведомления');
  nativePermission = result.permission;

  if (result.permission !== 'granted') {
    return {
      ok: false,
      reason: 'denied',
      message:
        transport === 'fcm'
          ? 'Уведомления отключены. Их можно включить в настройках телефона.'
          : 'Уведомления отключены. Их можно включить в «Настройках» iPhone.',
    };
  }
  if (!result.token) {
    // Permission granted and still no token: no network at launch, or a build
    // with no push entitlement. Two very different problems that look the same
    // from here, which is why the native error is carried through rather than
    // replaced with a guess.
    reportClientError({ kind: 'push', detail: 'register', message: result.error ?? 'no token' });
    return {
      ok: false,
      reason: 'failed',
      message: 'Не удалось зарегистрировать устройство. Попробуйте ещё раз.',
    };
  }

  // The second Apple token, for ringing a locked phone through CallKit. Null off
  // a shell that has no CallCentre — which is every Android one, PushKit having
  // no counterpart there — and never a reason to fail: a phone that cannot be
  // rung properly still gets the alert push.
  await api.subscribeNative(transport, result.token, await registerVoipToken());
  rememberPushToken(result.token);
  return { ok: true, message: 'Уведомления включены' };
}

async function subscribeAndSync(registration: ServiceWorkerRegistration): Promise<boolean> {
  const config = await api.pushConfig();
  if (!config.enabled || !config.publicKey) return false;

  const existing = await registration.pushManager.getSubscription();
  const subscription =
    existing ??
    (await registration.pushManager.subscribe({
      // Required by every browser that implements this, despite the name.
      userVisibleOnly: true,
      applicationServerKey: decodeKey(config.publicKey),
    }));

  await api.subscribePush(subscription.toJSON());
  return true;
}

/**
 * Must be called from a click handler. Safari will not even consider the
 * permission request outside a user gesture, and on iOS calling it from a
 * browser tab throws outright — hence the home-screen branch before anything
 * else happens.
 */
export async function enableNotifications(): Promise<EnableResult> {
  const env = pushEnvironment();

  if (env.needsHomeScreen) {
    return {
      ok: false,
      reason: 'needs-home-screen',
      message:
        'На iPhone уведомления работают только в установленном приложении. Нажмите «Поделиться», затем «На экран „Домой“», и откройте приложение с домашнего экрана.',
    };
  }

  if (env.isNative) {
    try {
      return await registerNative();
    } catch (error) {
      console.error('push: native registration failed', error);
      reportClientError({
        kind: 'push',
        detail: 'register',
        message: error instanceof Error ? `${error.name}: ${error.message}` : String(error),
      });
      return {
        ok: false,
        reason: 'failed',
        // withTimeout's own message names what stopped answering, and it is the
        // one worth showing: «нативная часть не отвечает» is a build mismatch,
        // and anything else is a request that failed.
        message: error instanceof Error ? error.message : 'Не удалось включить уведомления.',
      };
    }
  }

  if (!env.supported) {
    return {
      ok: false,
      reason: 'unsupported',
      message: 'Этот браузер не поддерживает уведомления.',
    };
  }

  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return {
        ok: false,
        reason: 'denied',
        message:
          'Уведомления отключены. Их можно включить в настройках браузера для этого сайта.',
      };
    }

    const registration = await navigator.serviceWorker.ready;
    const subscribed = await subscribeAndSync(registration);
    if (!subscribed) {
      return {
        ok: false,
        reason: 'failed',
        message: 'Уведомления недоступны: сервер не настроен.',
      };
    }

    return { ok: true, message: 'Уведомления включены' };
  } catch (error) {
    console.error('push: subscribe failed', error);
    // Permission was granted and it still did not work — the push service
    // refused, or the service worker never became ready. That is the failure
    // «уведомления не приходят» actually is, and it happens on the one device
    // it happens on, which is why the note has to be filed from there.
    reportClientError({
      kind: 'push',
      detail: 'subscribe',
      message: error instanceof Error ? `${error.name}: ${error.message}` : String(error),
    });
    return {
      ok: false,
      reason: 'failed',
      message: 'Не удалось включить уведомления. Попробуйте ещё раз.',
    };
  }
}

/**
 * Run on every launch. iOS drops push subscriptions silently — after an update,
 * after a long spell unused — and the app is never told. Re-posting whatever
 * the browser currently has keeps the server's copy true, and re-subscribing
 * when it has vanished repairs it. Neither needs a gesture, because permission
 * has already been granted by this point.
 */
export async function syncSubscription(): Promise<void> {
  const env = await refreshEnvironment();
  if (!env.supported || env.permission !== 'granted' || env.needsHomeScreen) return;

  try {
    if (env.isNative) {
      // No permission prompt happens here — iOS has already been asked and
      // answered, and `register` only re-asks Apple for a token. That token is
      // the thing being repaired: a restore from backup or a reinstall mints a
      // new one and the server's copy silently becomes an address nobody is at.
      const result = await withTimeout(
        nativePushPlugin()!.register(),
        PLUGIN_TIMEOUT_MS,
        'уведомления',
      );
      if (result.token) {
        // Both tokens re-posted together, because both rot the same way: a
        // restore or a reinstall mints new ones and tells nobody.
        await api.subscribeNative(nativeTransport(), result.token, await registerVoipToken());
        rememberPushToken(result.token);
      }
      await configureNativePush();
      void reportPushDiagnostics();
      return;
    }
    const registration = await navigator.serviceWorker.ready;
    await subscribeAndSync(registration);
  } catch (error) {
    // Offline at launch is the ordinary case; the next launch tries again.
    console.warn('push: subscription sync failed', error);
    // Filed anyway, because the ordinary case and the one that matters look
    // identical from here: this is the call that repairs a subscription iOS
    // dropped, and one that fails every launch is a phone that has silently
    // stopped receiving anything. The throttle keeps a genuinely offline
    // launch from being written down more than once.
    reportClientError({
      kind: 'push',
      detail: 'sync',
      message: error instanceof Error ? `${error.name}: ${error.message}` : String(error),
    });
  }
}

/**
 * Where the server is and who we are, handed down to the native half.
 *
 * A notification's buttons — «Ответить», «Прочитано», «Отклонить» — are handled
 * with no page running at all, so nothing can be asked for at the moment one is
 * pressed. The same arrangement the gallery makes for its background runs, and
 * the same obligation in the other direction: `forgetNativePush` clears it, and
 * every path that ends a session has to call one or the other.
 *
 * Never awaited by anything that matters and never fatal: a shell older than
 * this bundle has no such method, and what is lost there is the buttons, not
 * the notification.
 */
export async function configureNativePush(): Promise<void> {
  const plugin = nativePushPlugin();
  if (!plugin?.configure) return;
  try {
    await withTimeout(
      plugin.configure({ origin: apiOrigin, token: sessionToken() ?? '' }),
      PLUGIN_TIMEOUT_MS,
      'настройка уведомлений',
    );
  } catch (error) {
    console.warn('push: could not configure the native half', error);
  }
}

/**
 * What the OS thinks of our notifications, reported when the answer changes.
 *
 * There is no Mac in this loop and an iPhone tells nobody anything: a bug there
 * costs a tag, a TestFlight wait and a guess. So the phone says it out loud
 * instead, through the channel that already carries what a client cannot
 * otherwise report — and /admin becomes the place an iOS notification problem is
 * read rather than guessed at.
 *
 * Only on a change, because the healthy answer is the same on every launch and a
 * line per launch is a log nobody reads. The first launch after this ships
 * reports whatever is true then, which is the point.
 */
const DIAGNOSTICS_KEY = 'chat:push-diagnostics';

/**
 * Readings rather than settings — the part of the answer that is expected to be
 * different every time it is asked.
 *
 * «Only on a change» was defeated by two things at once, and one phone wrote a
 * row on every single launch: the comparison was made against the whole line,
 * and `delivered` is a count of what is in Notification Centre right now, which
 * moves whenever anything arrives or is swiped away. So the count stays in what
 * gets reported — it is the answer to «пуши приходят, но их не видно» and the
 * reason the field exists — and stays out of what decides whether to report.
 */
const VOLATILE_KEYS = new Set(['delivered']);

/**
 * The line, and the part of it worth comparing against last time.
 *
 * Sorted, which was the other half of the bug: this object crosses the
 * Capacitor bridge from a Swift dictionary, whose order is its own business and
 * differed from launch to launch. Eight identical facts in a new arrangement
 * are not a change, and string equality had no way to know that.
 *
 * Exported for the test; nothing else has a use for it.
 */
export function diagnosticsLines(state: Record<string, unknown>): {
  line: string;
  signature: string;
} {
  const entries = Object.entries(state).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  const format = (pairs: [string, unknown][]) =>
    pairs.map(([key, value]) => `${key}=${String(value)}`).join(' ');
  return {
    line: format(entries),
    signature: format(entries.filter(([key]) => !VOLATILE_KEYS.has(key))),
  };
}

export async function reportPushDiagnostics(): Promise<void> {
  const plugin = nativePushPlugin();
  if (!plugin?.diagnostics) return;
  try {
    const state = await withTimeout(plugin.diagnostics(), PLUGIN_TIMEOUT_MS, 'диагностика');
    const { line, signature } = diagnosticsLines(state);
    if (localStorage.getItem(DIAGNOSTICS_KEY) === signature) return;
    // What is stored is the signature and what is sent is the whole line, so
    // the count is readable in the row without being able to cause one. The
    // first launch after this ships compares against the old format and writes
    // once, which is the same trade the paragraph above already made.
    reportClientError({ kind: 'push', detail: 'os-settings', message: line });
    localStorage.setItem(DIAGNOSTICS_KEY, signature);
  } catch {
    // A shell that cannot answer is not worth a row of its own.
  }
}

/** The other half of the obligation above: no session, no credential. */
export async function forgetNativePush(): Promise<void> {
  const plugin = nativePushPlugin();
  if (!plugin?.configure) return;
  try {
    await withTimeout(
      plugin.configure({ origin: '', token: '' }),
      PLUGIN_TIMEOUT_MS,
      'настройка уведомлений',
    );
  } catch (error) {
    console.warn('push: could not clear the native half', error);
  }
}

/**
 * Whether this device currently holds a push subscription.
 *
 * Distinct from `Notification.permission`, and conflating the two is a real
 * trap: permission survives an unsubscribe, and on iOS the subscription can
 * vanish on its own while permission stays `granted`. Anything offering to turn
 * notifications on or off has to ask this, not the permission.
 */
export async function isSubscribed(): Promise<boolean> {
  // In the app the answer is the token this device last posted. There is
  // nothing to ask iOS: it holds a permission, not a subscription, and the
  // subscription is a row on the server keyed by a token only this side
  // remembers.
  if (isNative) return storedPushToken() !== null;

  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false;
  try {
    const registration = await navigator.serviceWorker.ready;
    return (await registration.pushManager.getSubscription()) !== null;
  } catch {
    return false;
  }
}

/** Turns notifications off on this device, both locally and on the server. */
export async function disableNotifications(): Promise<void> {
  if (isNative) {
    const token = storedPushToken();
    // Forgotten first, and unconditionally: the switch has to end up off even
    // if the request below fails, and a token the server still holds is a row
    // that sends to an app which will simply never show what it receives.
    rememberPushToken(null);
    // And the credential the buttons would have used. Nothing on this phone
    // will draw a notification again, so nothing should still be able to answer
    // one on its behalf.
    await forgetNativePush();
    if (token) await api.unsubscribePush(token).catch(() => {});
    return;
  }

  if (!('serviceWorker' in navigator)) return;
  // getRegistration rather than `ready`, which is the promise that never
  // settles when nothing is registered — and this is now on the way out of a
  // session, where a hang is a sign-out that does not finish.
  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();
  if (!subscription) return;

  const { endpoint } = subscription;
  await subscription.unsubscribe();
  await api.unsubscribePush(endpoint).catch(() => {
    // The local unsubscribe already stopped delivery; a stale row will be
    // pruned on its next 410 anyway.
  });
}

/** Whether a call may take over the screen here, and whether that is even a question. */
export interface CallScreenGrant {
  /**
   * Whether this device has the grant to give. False everywhere the question
   * does not arise — the web, iOS, and Android below 14, where declaring
   * USE_FULL_SCREEN_INTENT in the manifest is the whole of it.
   */
  applicable: boolean;
  granted: boolean;
}

/**
 * Whether an incoming call may take over the screen on this device.
 *
 * From Android 14 the grant belongs to apps the installer considers calling
 * apps, which one sideloaded from /download is not, so it has to be asked for.
 * Its absence costs the screen and not the call, and only since the ring became
 * a foreground service: that is what earns the CallStyle layout now, so the card
 * rings with its own buttons either way and what is lost is the phone lighting
 * up by itself. See CallRingService.
 *
 * Two answers rather than one, which is what a phone in somebody else's hands
 * taught this: «выдано» and «здесь такого разрешения нет» used to arrive as the
 * same `true`, so the settings screen could only ever mention the permission
 * while it was missing — and being unable to find the row was indistinguishable
 * from having nothing to fix. A shell too old to know the difference still says
 * one thing, and the one thing it can still be trusted on is a refusal: only an
 * Android 14 can report `granted: false` at all.
 */
export async function callScreenGrant(): Promise<CallScreenGrant> {
  const plugin = nativePushPlugin();
  if (!plugin?.fullScreenPermission) return { applicable: false, granted: true };
  try {
    return readCallScreenAnswer(
      await withTimeout(
        plugin.fullScreenPermission(),
        PLUGIN_TIMEOUT_MS,
        'разрешение на полный экран',
      ),
    );
  } catch {
    // A shell that cannot answer is not a shell to nag about it.
    return { applicable: false, granted: true };
  }
}

/**
 * The bridge's answer, read for both facts — including out of a shell that only
 * ever sends one.
 *
 * A refusal is self-describing: no platform but Android 14 can produce one, so
 * `granted: false` with nothing beside it still means the row belongs on screen.
 * A bare `granted: true` is the ambiguous half and is read as «нечего
 * показывать», which is what that build has always done — an old shell keeps
 * its old behaviour rather than gaining a row it cannot keep honest.
 *
 * Exported for the test; `callScreenGrant` is the way in.
 */
export function readCallScreenAnswer(answer: {
  granted: boolean;
  applicable?: boolean;
}): CallScreenGrant {
  return { applicable: answer.applicable ?? !answer.granted, granted: answer.granted };
}

/** The system page for the permission above. Does nothing where it has none. */
export async function openFullScreenSettings(): Promise<void> {
  await nativePushPlugin()?.openFullScreenSettings?.().catch(() => {});
}

/**
 * The app's own page in iOS Settings — the only way back from a refusal, since
 * iOS shows its permission alert exactly once. Does nothing on the web, where
 * the equivalent is a browser UI we cannot open.
 */
export async function openNotificationSettings(): Promise<void> {
  await nativePushPlugin()?.openSettings().catch(() => {});
}

/**
 * The nudge to drain, while the page is up.
 *
 * Silent on a shell with no such event — the drain on every return to the
 * foreground is what that shell has, and it covers everything except a tap on an
 * app that never left the screen.
 */
export function watchNativePushEvents(handler: () => void): () => void {
  const plugin = nativePushPlugin();
  if (!plugin?.addListener) return () => {};
  const handle = plugin.addListener('pushEvent', handler);
  return () => handle.remove();
}

/**
 * Notifications that have already been acted on, collected from the native
 * queue: a tap that opened the app, or one that arrived while it was on screen.
 *
 * Called on launch and on every return to the foreground. Draining is what
 * makes a tap survive a cold start — it is queued in Swift before the web view
 * exists, which no listener could have heard (see PushNotifications.swift).
 *
 * Each is acknowledged as it is read. On the web the service worker does this
 * the instant the notification is drawn; here the app can only speak when it is
 * running, which is what the note on `acked` in server/src/push.ts is about.
 */
export async function drainNativeNotifications(): Promise<string[]> {
  const plugin = nativePushPlugin();
  if (!plugin) return [];

  let events;
  try {
    ({ events } = await withTimeout(plugin.drainEvents(), PLUGIN_TIMEOUT_MS, 'уведомления'));
  } catch {
    // An older native half with no such method. Nothing to open and nothing to
    // report: notifications still arrive, they just cannot say so.
    return [];
  }

  const urls: string[] = [];
  for (const event of events) {
    if (event.deliveryId) {
      // Never awaited in a way that could cost the navigation — the same rule
      // the service worker follows: the measurement must not be able to break
      // the thing it measures.
      void api.ackPush(event.deliveryId).catch(() => {});
    }
    if (event.tapped && event.url) urls.push(event.url);
  }
  return urls;
}

/**
 * Short, unlike PLUGIN_TIMEOUT_MS beside it, because nothing here puts a dialog
 * on screen and nobody is waiting on the answer. Both calls below are told
 * rather than asked, and both are made often enough that sixty seconds of
 * pending promises against a shell too old to have the method would be a leak
 * measured in one per message.
 */
const TELL_TIMEOUT_MS = 5_000;

/**
 * Takes a category's notifications out of the shade — `chat:<id>` for a
 * conversation, `call` for a ring.
 *
 * The counterpart of showing one, and the half that was missing: a notification
 * is removed by a tap and by nothing else, so every message read inside the app
 * left its banner standing. On Android that is also the launcher badge, which
 * counts notifications rather than anything this app knows about unread
 * messages — hence «1» on an icon over a chat list with nothing in it.
 *
 * Never rejects. A shell older than this bundle has no such method and the
 * bridge answers by not answering at all (see withTimeout); a browser without a
 * worker has nothing to clear. Both are a banner that stays, which is what
 * happened before this existed.
 */
export async function clearNotifications(tag: string): Promise<void> {
  const plugin = nativePushPlugin();
  if (plugin) {
    await withTimeout(plugin.clear({ tag }), TELL_TIMEOUT_MS, 'уведомления').catch(() => {});
    return;
  }

  if (!('serviceWorker' in navigator)) return;
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    for (const notification of (await registration?.getNotifications({ tag })) ?? []) {
      notification.close();
    }
  } catch {
    // Not a secure context, or no registration yet. Nothing was shown either.
  }
}

/**
 * Tells the native half which conversation is on screen, so it can show a
 * notification from any *other* one.
 *
 * Does nothing on the web: there the same question is asked the other way round
 * — the service worker asks the page at the moment a push arrives (WHICH_CHAT
 * in client/src/sw.ts), because a worker woken by a push is a fresh worker with
 * no memory of anything the page told it.
 */
export async function reportActiveConversation(conversationId: string | null): Promise<void> {
  const plugin = nativePushPlugin();
  if (!plugin) return;
  await withTimeout(
    plugin.setActiveConversation({ conversationId }),
    TELL_TIMEOUT_MS,
    'открытый чат',
  ).catch(() => {});
}
